import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import VehicleDropdown from '../../shared/common-dropdown/VehicleDropdown';
import PaymentTypeDropdown from '../../shared/common-dropdown/PaymentTypeDropdown';
import VehicleEnquiryGrid from './VehicleEnquiryGrid';
import FinanceDetails from '../../shared/finance/FinanceDetails';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { getStorageValues } from '../../shared/CommonFunctions';
import { useMasterData } from '../../utils/useMasterData';

const INITIAL_FORM_STATE = {
  CustomerName: '',
  MobileNo: '',
  EnquirySource: 'walking',
  EnquirySourceLabel: 'Walking',
  ReferenceName: '',
  Area: '',
  VehicleId: '',
  VehicleLabel: '',
  VehicleCost: '',
  PaymentType: 'cash',
  PaymentTypeLabel: 'Cash',
  DownPayment: '',
  EMITenure: '',
  EMIAmount: '',
  ProcessingFees: '',
  InterestRate: ''
};

function AddEnquiry({ sendDataToParent, onLoad, initialValues = null, onLoanSummary, onRegisterLoanSummaryApply }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [vehicleEntries, setVehicleEntries] = useState([]);
  const [vehicleNameError, setVehicleNameError] = useState('');
  const [duplicateError, setDuplicateError] = useState('');
  const [referenceNameError, setReferenceNameError] = useState('');
  const [loanSummaryError, setLoanSummaryError] = useState('');
  const [financePartners, setFinancePartners] = useState([]);
  const [financePartnersError, setFinancePartnersError] = useState('');
  const [selectedFinancePartnerId, setSelectedFinancePartnerId] = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const initialValuesKeyRef = useRef(null);
  const { items: interestRateItems, loading: interestRateLoading } = useMasterData('interestRates');
  const { options: enquirySourceOptions, loading: enquirySourceLoading } = useMasterData('enquirySource');
  const [accessoriesItems, setAccessoriesItems] = useState([]);
  const [accessoriesLoading, setAccessoriesLoading] = useState(false);
  const [accessoriesError, setAccessoriesError] = useState('');

  const interestRateOptions = useMemo(() => {
    return (interestRateItems || []).map((item) => ({
      value: item.ValueName ?? '',
      label: item.DisplayName || item.ValueName || '',
      id: item.MasterValueId
    }));
  }, [interestRateItems]);

  const selectedInterestRate = useMemo(() => {
    if (!formData.InterestRate) return null;
    const valueStr = formData.InterestRate.toString();
    return interestRateOptions.find((opt) => opt.value?.toString() === valueStr) || null;
  }, [formData.InterestRate, interestRateOptions]);

  const fetchFinancePartners = useCallback(async () => {
    try {
      setFinancePartnersError('');
      const response = await axios.get(`${getApiBaseUrl()}/financepartner/getAll`, {
        params: { UserId: getStorageValues('userId') }
      });
      const payload = response?.data?.data;
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : [];
      setFinancePartners(rows);
    } catch (error) {
      setFinancePartners([]);
      setFinancePartnersError('Unable to load finance partners.');
    }
  }, []);

  const fetchAccessories = useCallback(async () => {
    try {
      setAccessoriesLoading(true);
      setAccessoriesError('');
      const response = await axios.get(`${getApiBaseUrl()}/handoveritems/getAll`, {
        params: { UserId: getStorageValues('userId') }
      });
      const payload = response?.data?.data;
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : [];
      const accessoryRows = rows.filter((item) => (item.HandoverItemType || '').toLowerCase() === 'accessories');
      setAccessoriesItems(accessoryRows);
    } catch (error) {
      setAccessoriesItems([]);
      setAccessoriesError('Unable to load accessories.');
    } finally {
      setAccessoriesLoading(false);
    }
  }, []);

  const updateFieldValue = useCallback((name, value, options = {}) => {
    setFormData((prev) => {
      const nextState = {
        ...prev,
        [name]: value,
      };
      if (!options.skipParent) {
        sendDataToParent((prevState) => ({
          ...prevState,
          [name]: value,
        }));
      }
      return nextState;
    });
  }, [sendDataToParent]);

  const handleChange = (e) => {
    const { name, value } = e.currentTarget;
    updateFieldValue(name, value);
  };

  const selectedEnquirySource = useMemo(() => {
    const valueStr = (formData.EnquirySource ?? '').toString();
    return (enquirySourceOptions || []).find((opt) => opt.value?.toString() === valueStr) || null;
  }, [enquirySourceOptions, formData.EnquirySource]);

  const resetFinanceFields = () => {
    ['DownPayment', 'EMITenure', 'EMIAmount', 'ProcessingFees', 'InterestRate'].forEach((field) => {
      updateFieldValue(field, '', { skipParent: false });
    });
    setSelectedFinancePartnerId(null);
  };

  const resetVehicleFields = () => {
    setFormData((prev) => {
      const nextState = {
        ...prev,
        VehicleId: '',
        VehicleLabel: '',
        VehicleCost: '',
        PaymentType: 'cash',
        PaymentTypeLabel: 'Cash',
        DownPayment: '',
        EMITenure: '',
        EMIAmount: '',
        ProcessingFees: '',
        InterestRate: ''
      };
      sendDataToParent((parentPrev) => ({
        ...parentPrev,
        ...nextState,
      }));
      return nextState;
    });
    setVehicleNameError('');
    setDuplicateError('');
    setEditingEntryId(null);
  };

  const handleDropdownChange = (fieldName, labelField) => (option) => {
    const value = option ? option.value : '';
    const label = option ? option.label : '';
    updateFieldValue(fieldName, value);
    if (labelField) {
      updateFieldValue(labelField, label, { skipParent: true });
    }
    if (fieldName === 'PaymentType' && value !== 'loan') {
      resetFinanceFields();
    }

    if (fieldName === 'EnquirySource') {
      setReferenceNameError('');
      if ((label || '').toLowerCase() !== 'reference' && (value || '').toLowerCase() !== 'reference') {
        updateFieldValue('ReferenceName', '');
      }
    }
  };

  const handleVehicleChange = (option) => {
    const value = option ? option.value : '';
    const label = option ? option.label : '';
    updateFieldValue('VehicleId', value);
    updateFieldValue('VehicleLabel', label, { skipParent: true });
    setVehicleNameError('');
    setDuplicateError('');
  };

  const handleFinanceInputChange = (field) => (e) => {
    updateFieldValue(field, e.currentTarget.value);
  };

  useEffect(() => {
    fetchAccessories();
  }, [fetchAccessories]);

  const paymentType = formData.PaymentType;

  const handleFinancePartnerClick = (partner) => {
    if (!partner) return;
    const rate = partner.InterestRate ?? '';
    const downPayment = partner.MinimumDownPayment ?? '';
    updateFieldValue('InterestRate', rate === null ? '' : rate.toString());
    updateFieldValue('DownPayment', downPayment === null ? '' : downPayment.toString());
    const key = partner.FinancePartnerId ?? partner.FinancePartnerName ?? null;
    setSelectedFinancePartnerId(key);
  };

  const parsedFinanceValues = useMemo(() => {
    const parseNumber = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return {
      vehicleCost: parseNumber(formData.VehicleCost),
      downPayment: parseNumber(formData.DownPayment),
      tenureMonths: parseInt(formData.EMITenure, 10) || 0,
      processingFees: parseNumber(formData.ProcessingFees),
      interestRate: parseNumber(formData.InterestRate)
    };
  }, [formData.VehicleCost, formData.DownPayment, formData.EMITenure, formData.ProcessingFees, formData.InterestRate]);

  const calculateLoanSummary = () => {
    const { vehicleCost, downPayment, tenureMonths, processingFees, interestRate } = parsedFinanceValues;

    if (!vehicleCost) throw new Error('Enter vehicle cost to calculate loan summary.');
    if (tenureMonths <= 0) throw new Error('Enter EMI tenure (in months).');
    if (interestRate <= 0) throw new Error('Enter annual interest rate.');

    const principal = Math.max(vehicleCost - downPayment, 0) + processingFees;
    const monthlyRate = interestRate / 12 / 100;
    const factor = Math.pow(1 + monthlyRate, tenureMonths);
    const emi = monthlyRate === 0 || !Number.isFinite(factor)
      ? principal / tenureMonths
      : (principal * monthlyRate * factor) / (factor - 1);

    return {
      vehicleCost,
      downPayment,
      processingFees,
      interestRate,
      tenureMonths,
      loanAmount: principal,
      monthlyEmi: Number((emi || 0).toFixed(2)),
      totalPayable: Number(((emi || 0) * tenureMonths).toFixed(2))
    };
  };

  const handleLoanSummaryClick = () => {
    try {
      const summary = calculateLoanSummary();
      setLoanSummaryError('');
      if (typeof onLoanSummary === 'function') {
        onLoanSummary(summary);
      }
    } catch (err) {
      setLoanSummaryError(err.message || 'Unable to calculate loan summary.');
    }
  };

  const applyLoanSummaryEmi = useCallback((summary) => {
    if (!summary) {
      return;
    }
    const emiValue = summary.monthlyEmi ?? '';
    updateFieldValue('EMIAmount', emiValue !== '' ? String(emiValue) : '', { skipParent: false });
  }, [updateFieldValue]);

  useEffect(() => {
    if (typeof onRegisterLoanSummaryApply === 'function') {
      onRegisterLoanSummaryApply(applyLoanSummaryEmi);
      return () => onRegisterLoanSummaryApply(null);
    }
  }, [applyLoanSummaryEmi, onRegisterLoanSummaryApply]);

  useEffect(() => {
    fetchFinancePartners();
  }, [fetchFinancePartners]);

  const handleAddEnquiryRow = (e) => {
    e.preventDefault();
    if (!formData.VehicleId) {
      setVehicleNameError('Vehicle is required');
      return;
    }
    const duplicateExists = vehicleEntries.some(
      (entry) => entry.VehicleId === formData.VehicleId && entry.id !== editingEntryId
    );
    if (duplicateExists) {
      setDuplicateError('An entry for this vehicle already exists.');
      return;
    }
    setVehicleNameError('');
    setDuplicateError('');
    const entryPayload = {
      id: editingEntryId || Date.now(),
      VehicleId: formData.VehicleId,
      Vehicle: formData.VehicleLabel || '',
      VehicleCost: formData.VehicleCost || '',
      PaymentType: formData.PaymentTypeLabel || formData.PaymentType || '',
      DownPayment: formData.DownPayment || '',
      EMITenure: formData.EMITenure || '',
      EMIAmount: formData.EMIAmount || '',
      ProcessingFees: formData.ProcessingFees || '',
      InterestRate: formData.InterestRate || '',
      VehicleNameValue: formData.VehicleLabel || '',
      PaymentTypeValue: formData.PaymentType,
    };

    setVehicleEntries((prev) => {
      const next = editingEntryId
        ? prev.map((row) => (row.id === editingEntryId ? entryPayload : row))
        : [...prev, entryPayload];
      sendDataToParent((prevState) => ({
        ...prevState,
        VehicleEnquiries: next,
      }));
      return next;
    });

    resetVehicleFields();
  };

  const handleDeleteEnquiryRow = (row) => {
    setVehicleEntries((prev) => {
      const next = prev.filter((entry) => entry.id !== row.id);
      sendDataToParent((prevState) => ({
        ...prevState,
        VehicleEnquiries: next,
      }));
      return next;
    });
    if (editingEntryId && row.id === editingEntryId) {
      resetVehicleFields();
    }
  };

  const handleEditVehicleEntry = (row) => {
    setEditingEntryId(row.id);
    setVehicleNameError('');
    setDuplicateError('');
    setFormData((prev) => ({
      ...prev,
      VehicleId: row.VehicleId ?? row.VehicleNameValue ?? '',
      VehicleLabel: row.VehicleLabel ?? row.Vehicle ?? row.VehicleNameValue ?? row.VehicleName ?? '',
      VehicleCost: row.VehicleCost ?? '',
      PaymentType: row.PaymentTypeValue ?? row.PaymentType ?? 'cash',
      PaymentTypeLabel: row.PaymentType ?? 'Cash',
      DownPayment: row.DownPayment ?? '',
      EMITenure: row.EMITenure ?? '',
      EMIAmount: row.EMIAmount ?? '',
      ProcessingFees: row.ProcessingFees ?? '',
      InterestRate: row.InterestRate ?? ''
    }));
  };

  useEffect(() => {
    const ADD_MODE_KEY = '__add__';
    const incomingKey = initialValues?.EnquiryId ?? (initialValues ? '__static__' : null);

    if (initialValues) {
      if (initialValuesKeyRef.current === incomingKey) {
        return;
      }

      const headerState = {
        CustomerName: initialValues.CustomerName ?? '',
        MobileNo: initialValues.MobileNo ?? '',
        EnquirySource: initialValues.EnquirySource ?? 'walking',
        EnquirySourceLabel: initialValues.EnquirySourceLabel ?? '',
        ReferenceName: initialValues.ReferenceName ?? '',
        Area: initialValues.Area ?? ''
      };
      const mergedState = {
        ...INITIAL_FORM_STATE,
        ...headerState
      };
      setFormData(mergedState);
      const detailRows = initialValues.VehicleEnquiries ?? [];
      setVehicleEntries(detailRows);
      sendDataToParent((prevState) => ({
        ...prevState,
        ...headerState,
        VehicleEnquiries: detailRows
      }));
      setVehicleNameError('');
      setDuplicateError('');
      setEditingEntryId(null);
      initialValuesKeyRef.current = incomingKey;
      return;
    }

    if (initialValuesKeyRef.current === ADD_MODE_KEY) {
      return;
    }

    onLoad?.(undefined);
    setFormData({ ...INITIAL_FORM_STATE });
    setVehicleEntries([]);
    sendDataToParent((prevState) => ({
      ...prevState,
      ...INITIAL_FORM_STATE,
      VehicleEnquiries: [],
    }));
    setVehicleNameError('');
    setDuplicateError('');
    setReferenceNameError('');
    setReferenceNameError('');
    setEditingEntryId(null);
    initialValuesKeyRef.current = ADD_MODE_KEY;
  }, [initialValues, onLoad, sendDataToParent]);

  useEffect(() => {
    if (!formData.EnquirySourceLabel) {
      const label = selectedEnquirySource?.label || '';
      if (label) {
        updateFieldValue('EnquirySourceLabel', label, { skipParent: true });
      }
    }
  }, [formData.EnquirySourceLabel, selectedEnquirySource, updateFieldValue]);

  useEffect(() => {
    const isReference = (selectedEnquirySource?.label || '').toLowerCase() === 'reference'
      || (formData.EnquirySource || '').toString().toLowerCase() === 'reference';
    if (!isReference) {
      setReferenceNameError('');
    }
  }, [formData.EnquirySource, selectedEnquirySource]);

  return (
    <>
      <div className="row">
        <div className="col-lg-8 col-sm-6 col-12 mb-3">
          <label className="form-label">
            Customer Name<span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="CustomerName"
            value={formData.CustomerName}
            onChange={handleChange}
          />
        </div>
        <div className="col-lg-4 col-sm-6 col-12 mb-3">
          <label className="form-label">
            Mobile No
          </label>
          <input
            type="text"
            className="form-control"
            name="MobileNo"
            value={formData.MobileNo}
            onChange={handleChange}
          />
        </div>

        <div className="col-lg-4 col-sm-6 col-12 mb-3">
          <label className="form-label">Enquiry Source</label>
          <Select
            isLoading={enquirySourceLoading}
            options={enquirySourceOptions}
            value={selectedEnquirySource}
            onChange={handleDropdownChange('EnquirySource', 'EnquirySourceLabel')}
            placeholder="Select Enquiry Source"
          />
        </div>

        {(selectedEnquirySource?.label || '').toLowerCase() === 'reference' && (
          <div className="col-lg-4 col-sm-6 col-12 mb-3">
            <label className="form-label">
              Reference Name<span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="ReferenceName"
              value={formData.ReferenceName}
              onChange={(e) => {
                setReferenceNameError('');
                handleChange(e);
              }}
              onBlur={() => {
                if (!String(formData.ReferenceName || '').trim()) {
                  setReferenceNameError('Reference Name is required');
                }
              }}
            />
            {referenceNameError && (
              <div className="text-danger small mt-1">{referenceNameError}</div>
            )}
          </div>
        )}

        <div className="col-lg-4 col-sm-6 col-12 mb-3">
          <label className="form-label">Area</label>
          <input
            type="text"
            className="form-control"
            name="Area"
            value={formData.Area}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6 col-sm-6 col-12 mb-6">
          <VehicleDropdown
            onChangeDropdown={handleVehicleChange}
            selectedValue={formData.VehicleId}
          />
          {vehicleNameError && (
            <div className="text-danger small mt-1">{vehicleNameError}</div>
          )}
        </div>
        <div className="col-lg-3 col-sm-6 col-12 mb-3">
          <label className="form-label">
            Vehicle Cost
          </label>
          <input
            type="text"
            className="form-control"
            name="VehicleCost"
            value={formData.VehicleCost}
            onChange={handleChange}
          />
        </div>

          <div className="col-lg-3 col-sm-6 col-12 mb-3">
          <PaymentTypeDropdown
            onChangeDropdown={handleDropdownChange('PaymentType', 'PaymentTypeLabel')}
            selectedValue={paymentType}
          />
        </div>

        {paymentType === 'loan' && (
          <div className="col-12">
            <FinanceDetails
              financePartners={financePartners}
              financePartnersError={financePartnersError}
              selectedFinancePartnerId={selectedFinancePartnerId}
              onFinancePartnerClick={handleFinancePartnerClick}
              downPaymentValue={formData.DownPayment}
              emiTenureValue={formData.EMITenure}
              processingFeesValue={formData.ProcessingFees}
              interestRateValue={formData.InterestRate}
              emiAmountValue={formData.EMIAmount}
              onDownPaymentChange={handleFinanceInputChange('DownPayment')}
              onEmiTenureChange={handleFinanceInputChange('EMITenure')}
              onProcessingFeesChange={handleFinanceInputChange('ProcessingFees')}
              onInterestRateChange={(option) => updateFieldValue('InterestRate', option ? option.value : '')}
              onEmiAmountChange={handleFinanceInputChange('EMIAmount')}
              interestRateOptions={interestRateOptions}
              interestRateLoading={interestRateLoading}
              selectedInterestRate={selectedInterestRate}
              onCalculateEmi={handleLoanSummaryClick}
              loanSummaryError={loanSummaryError}
              emiTenureLabel="EMI Tenure Month"
            />
          </div>
        )}
        <div className="col-12">
          <div className="enq-form mb-4">
            <button type="button" className="btn btn-cancel me-2" onClick={resetVehicleFields}>
              {editingEntryId ? 'Cancel Edit' : 'Reset'}
            </button>
            <button type="button" className="btn btn-submit" onClick={handleAddEnquiryRow}>
              {editingEntryId ? 'Update Vehicle' : 'Add Enquiry'}
            </button>
            {duplicateError && (
              <div className="text-danger small mt-2">{duplicateError}</div>
            )}
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="card-body">
            <nav className="nav nav-style-6 nav-pills mb-3 nav-justified d-sm-flex d-block" role="tablist">
              <a className="nav-link active" data-bs-toggle="tab" role="tab" aria-current="page" href="#nav-products-justified" aria-selected="true">VEHICLE ENQUIRY DETAILS</a>
              <a className="nav-link" data-bs-toggle="tab" role="tab" href="#nav-cart-justified" aria-selected="false" tabIndex="-1">VEHICLE ACCESSORIES </a>
            </nav>
            <div className="tab-content">
              <div className="tab-pane text-muted active show" id="nav-products-justified" role="tabpanel">
                <VehicleEnquiryGrid
                  data={vehicleEntries}
                  onDelete={handleDeleteEnquiryRow}
                  onEdit={handleEditVehicleEntry}
                  onAction={"Edit"}
                />
              </div>
              <div className="tab-pane text-muted" id="nav-cart-justified" role="tabpanel">
                <div className="acces-box">
                  <div className="acess">
                    {accessoriesLoading && (
                      <p className="col-12 text-muted">Loading accessories…</p>
                    )}
                    {!accessoriesLoading && accessoriesItems?.length === 0 && (
                      <p className="col-12 text-muted">No accessories configured.</p>
                    )}
                    {accessoriesError && (
                      <p className="col-12 text-danger">{accessoriesError}</p>
                    )}
                    {(accessoriesItems || []).map((item) => (
                      <p className="col-md-2" key={item.HandoverItemId || item.HandoverItemTitle}>
                        {(item.HandoverItemTitle || '').toUpperCase()}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AddEnquiry
